<template>
  <div class="border-b-1 border-border-light ">
    <!-- In Progress Badge -->
    <div class="bg-confirm-surface text-sm text-confirm p-2 flex items-center gap-2">
      <div class="bg-confirm-surface">
        <i class="text-confirm fas fa-circle-check"></i>
      </div>
      <div>
        In progress
      </div>
    </div>

    <!-- Event Header -->
    <div class="flex gap-2 mb-0 mt-3 w-full overflow-hidden">
      <!-- Event Logo -->
      <div class="w-[80px] flex-shrink-0">
        <AppEventLogo :is-small="true" :event-id="event?.id" :event-image="event?.eventSettings?.eventImage" />
      </div>
      <!-- Event Name and Details -->
      <div class="flex-1 min-w-0 overflow-hidden">
        <div class="flex justify-between w-full">
          <div class="min-w-0 overflow-hidden">
            <h3 class="text-normal-emphasis font-bold truncate max-w-full">{{ event.name }}</h3>

            <!-- Next Stop Section -->
            <div class="text-sm">
              <div class="text-normal-emphasis font-semibold">Next stop</div>
              <div v-if="stopsInfo.nextStop.value" class="flex items-center gap-1 w-full overflow-hidden">
                <span class="font-medium truncate max-w-[calc(100%-5px)]">{{ stopsInfo.nextStop.value.order }}. {{
                  stopsInfo.nextStop.value.name }}</span>
              </div>
              <div v-else-if="nextStopNumber && nextStopName" class="flex items-center gap-1 w-full overflow-hidden">
                <span class="font-medium truncate max-w-[calc(100%-5px)]">{{ nextStopNumber }}. {{ nextStopName
                }}</span>
              </div>
              <div v-else class="flex items-center gap-1">
                <span class="font-medium">No more stops</span>
              </div>
              <div class="flex items-center gap-1 text-normal-emphasis font-semibold truncate">
                <span v-if="stopsInfo.nextStopTime.value">Arrival: {{ formatTime(new Date(stopsInfo.nextStopTime.value))
                  }}</span>
                <span v-else-if="nextStopTime">Arrival: {{ nextStopTime }}</span>
              </div>
            </div>
          </div>
          <!-- Right Arrow -->
          <div class="w-[20px] flex items-center justify-end flex-shrink-0">
            <i class="fas fa-angle-right text-normal-reduced text-xl"></i>
          </div>
        </div>
        <div v-if="!userWaiverRequired && bestParticipant?.hand" class="mt-0">
          <div class="text-normal-emphasis font-semibold text-sm">Your hand (current rank: {{ bestRank.position }})
          </div>
        </div>

      </div>
    </div>
    <!-- Hand Section -->
    <div v-if="!userWaiverRequired && bestParticipant?.hand" class="mt-0 flex justify-center items-center w-full">
          <HandPreview :hand="bestParticipant.hand" :extra-card="event.eventSettings.enableExtraCard && bestParticipant.extraCard" />
      </div>

    <div v-if="userWaiverRequired">
      <AppLinkMessage>
        <span class="text-xs text-normal-emphasis font-semibold">Please view and accept the event
          waiver.</span>
      </AppLinkMessage>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { PokerRunEvent } from '@models/PokerRunEvent';
import { EventParticipant } from '@models/EventParticipant';
import useStops from '@/modules/useStops';
import useDate from '@/modules/useDate';
import HandPreview from '@/components/HandPreview.vue';
import AppEventLogo from '@/components/AppEventLogo.vue';
import { Hand as HandType } from '@models/Hand';
import useObservableEventParticipant from '@/composables/useObservableEventParticipant';
import { useUserProfileStore } from '@/store/UserProfile';

const props = defineProps<{
  event: PokerRunEvent;
}>();

const eventRef = ref(props.event);

// Watch for changes in props and update the refs
watch(() => props.event, (newEvent) => {
  eventRef.value = newEvent;
});

// Get the formatTime function from useDate
const { formatTime } = useDate();

const { profile } = useUserProfileStore();

const { bestParticipant, bestRank, userWaiverRequired } = useObservableEventParticipant(props.event, profile.id);

// Use the useStops composable to get reactive stop information
const stopsInfo = useStops(eventRef, bestParticipant);
</script>
